import SplashLoader from '@components/atoms/SplashLoader';
import { OutsetaAudEnum } from '@models/common/outsetaRealm.enum';
import { IOutsetaToken } from '@models/outseta/outseta';
import NoAccountPage from '@pages/auth/NoAccountPage';
import PatientEnrollmentPage from '@pages/open-routes/patient-enrollment';
import PatientFeedbackPage from '@pages/open-routes/patient-feedback';
import { jwtDecode } from 'jwt-decode';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { isClerkAuthEnabled } from 'src/utils/clerkAuth';
import { isCustomerCareEnabled } from 'src/utils/customerCare';

import OpenRoute from './OpenRoute';
import paths from './paths';
import { PrivateRoute } from './PrivateRoute';
import { PropertyGuardRoute } from './PropertyGuardRoute';
import { PublicRoute } from './PublicRoute';
import { SpartacusAppGuard } from './spartacusRoutes/app/SpartacusAppGuard';
import OnboardingSpartacusV2Routes from './spartacusRoutes/OnboardingSpartacusV2Routes';

// Lazy-loaded route components for code-splitting
const ReviewCampaign = lazy(() => import('@pages/open-routes/review-campaign'));
const Unsubscribe = lazy(() => import('@pages/open-routes/unsubscribe'));
const AppRoute = lazy(() => import('./routes/app/AppRoute').then((m) => ({ default: m.AppRoute })));
const InsightsRoutes = lazy(() => import('./routes/app/InsightsRoutes'));
const HumanPlusRoutes = lazy(() => import('./routes/app/HumanPlusRoutes'));
const ListingRoutes = lazy(() => import('./routes/app/ListingRoutes'));
const OrgSettingsRoutes = lazy(() => import('./routes/app/OrgSettingsRoutes'));
const ReputationRoutes = lazy(() => import('./routes/app/ReputationRoutes'));
const SocialRoutes = lazy(() => import('./routes/app/SocialRoutes'));
const JooiceaiRoutes = lazy(() => import('./routes/app/JooiceaiRoutes'));
const MessagesRoutes = lazy(() => import('./routes/app/MessagesRoutes'));
const TestRoutes = lazy(() => import('./routes/app/TestRoutes'));
const OnboardingRoutes = lazy(() => import('./routes/OnboardingRoutes'));
const LoginPage = lazy(() => import('@pages/auth/LoginPage'));
const ClerkSsoCallbackPage = lazy(() => import('@pages/auth/ClerkSsoCallbackPage'));
const PurchasePage = lazy(() => import('@pages/auth/PurchasePage'));
const ClerkEmailLinkCallbackPage = lazy(() => import('@pages/auth/ClerkEmailLinkCallbackPage'));
const DashboardHomePage = lazy(() => import('@pages/app/dashboard'));
const AccountPage = lazy(() => import('@pages/app/account'));
const InstallPWA = lazy(() => import('@pages/app/install'));
const TrialOnboardingPage = lazy(() => import('@pages/auth/TrialOnboardingPage'));
const MagicLinkPage = lazy(() => import('@pages/auth/MagicLinkPage'));
//spartacus routes
const SpartacusRoutes = lazy(() => import('./spartacusRoutes/app/SpartacusRoutes'));
const SuggestionRoutes = lazy(() => import('./spartacusRoutes/app/SuggestionRoutes'));
const ReviewsRoutes = lazy(() => import('./spartacusRoutes/app/ReviewsRoutes'));
const YourLocationRoutes = lazy(() => import('./spartacusRoutes/app/YourLocationRoutes'));
const SettingsRoutes = lazy(() => import('./spartacusRoutes/app/SettingsRoutes'));
const SocialPlannerRoutes = lazy(() => import('./spartacusRoutes/app/SocialRoutes'));
const SocialPlannerEditor = lazy(() =>
  import('@pages/app/social-planner/components/editor').then((m) => ({ default: m.Editor })),
);
const InboxRoutes = lazy(() => import('./spartacusRoutes/app/InboxRoutes'));
const CampaignsRoutes = lazy(() => import('./spartacusRoutes/app/CampaignsRoutes'));

const MainRouter = () => {
  const onDevEnvironment =
    window.location.hostname === 'localhost' ||
    window.location.hostname === 'dev.app.jooice.com' ||
    window.location.hostname.startsWith('192.168');
  return (
    <Suspense fallback={<SplashLoader />}>
      <Routes>
        <Route path="open" element={<OpenRoute />}>
          <Route path="unsubscribe" element={<Unsubscribe />} />
        </Route>
        <Route path="revs/:slug" element={<ReviewCampaign />} />
        <Route path="enroll/:encryptedId" element={<PatientEnrollmentPage />} />
        {isCustomerCareEnabled() && <Route path="patient-feedback/:orgToken" element={<PatientFeedbackPage />} />}

        <Route path="suggestions" element={<Navigate to="/app/suggestions" replace />} />
        <Route path="reviews-inbox" element={<Navigate to="/app/reviews-inbox" replace />} />

        {/* Clerk callbacks must always be consumed (they can be a re-login or a
            different account), even when a stale Outseta token already exists —
            PublicRoute would otherwise bounce them to dashboard before Clerk can
            read the callback URL and finish authentication. */}
        <Route path="auth/no-account" element={<NoAccountPage />} />
        <Route path="auth/trial" element={<TrialOnboardingPage />} />
        <Route path="auth/clerk/email-link-callback" element={<ClerkEmailLinkCallbackPage />} />
        <Route path="auth/clerk/sso-callback" element={<ClerkSsoCallbackPage />} />
        <Route path="purchase" element={<PurchasePage />} />
        <Route element={<PublicRoute />}>
          <Route path="auth/*" element={<LoginPage />} />
        </Route>
        {/* Not under PublicRoute: a magic-link must always be consumed (it can be a
            re-login or a different account), even when a stale token already exists —
            PublicRoute would otherwise bounce it to dashboard before the new
            access_token in the URL is ever read. */}
        <Route path="magic-link" element={<MagicLinkPage />} />

        <Route element={<PrivateRoute />}>
          <Route
            element={
              <PropertyGuardRoute
                predicate={() => {
                  if (isClerkAuthEnabled()) return true;
                  const token = localStorage.getItem('Outseta.nocode.accessToken');
                  if (!token) return false;
                  // Round 9 review (M2): `access_token` reaches localStorage straight from a
                  // URL query param (MagicLinkPage) with no server-side shape verification —
                  // a crafted non-JWT value throws inside jwtDecode and, unhandled, blanks the
                  // whole app one hop from the new primary landing page. Decode failure is not
                  // meaningfully different from "no valid token": treat it the same way.
                  try {
                    const decoded: IOutsetaToken = jwtDecode(token);
                    return Boolean(decoded.aud !== OutsetaAudEnum.SPARTACUS);
                  } catch {
                    return false;
                  }
                }}
                redirectTo={paths.spartacus.suggestions}
              />
            }
          >
            <Route path="onboarding/*" element={<OnboardingRoutes />} />
            <Route path="app" element={<AppRoute />}>
              {onDevEnvironment && <Route path="test/*" element={<TestRoutes />} />}
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardHomePage />} />
              <Route path="listing/*" element={<ListingRoutes />} />
              <Route path="reputation/*" element={<ReputationRoutes />} />
              <Route path="social/*" element={<SocialRoutes />} />
              <Route path="insights/*" element={<InsightsRoutes />} />
              <Route path="jooiceai/*" element={<JooiceaiRoutes />} />
              <Route path="org-settings/*" element={<OrgSettingsRoutes />} />
              <Route path="account" element={<AccountPage />} />
              <Route path="install" element={<InstallPWA />} />
              <Route path="human-plus/*" element={<HumanPlusRoutes />} />
              <Route path="messages/*" element={<MessagesRoutes />} />
            </Route>
          </Route>
          <Route
            element={
              <PropertyGuardRoute
                predicate={() => {
                  if (isClerkAuthEnabled()) return false;
                  const token = localStorage.getItem('Outseta.nocode.accessToken');
                  if (!token) return false;
                  try {
                    const decoded: IOutsetaToken = jwtDecode(token);
                    return Boolean(decoded.aud === OutsetaAudEnum.SPARTACUS);
                  } catch {
                    return false;
                  }
                }}
                redirectTo={paths.app.dashboard}
              />
            }
          >
            <Route path="spartacus/onboarding/*" element={<OnboardingSpartacusV2Routes />} />
            <Route path="spartacus/app/*" element={<SpartacusRoutes />}>
              <Route path="suggestions/*" element={<SuggestionRoutes />} />
              <Route path="reviews-inbox/*" element={<ReviewsRoutes />} />
              <Route path="your-location/*" element={<YourLocationRoutes />} />
              <Route path="settings/*" element={<SettingsRoutes />} />
              <Route path="social-planner/*" element={<SocialPlannerRoutes />} />
              {isCustomerCareEnabled() && (
                <>
                  <Route path="inbox/*" element={<InboxRoutes />} />
                  <Route path="campaigns/*" element={<CampaignsRoutes />} />
                </>
              )}
            </Route>
            <Route
              path={paths.spartacus.socialPlannerEditor}
              element={
                <SpartacusAppGuard>
                  <SocialPlannerEditor />
                </SpartacusAppGuard>
              }
            />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/auth" />} />
      </Routes>
    </Suspense>
  );
};

export default MainRouter;
